import { computed, getCurrentScope, isRef, onScopeDispose, ref, unref } from 'vue'
import { watchDeep } from '@vueuse/core'
import QueryEncoder from '@/utils/Encoders/QueryEncoder'

/**
 * A simple wrapper around the QueryEncoder object. Makes it easy to work with simple single variables that are bound
 * to the url query parameters.
 * vueuse have similar looking tools, but they don't work correctly and are more a deep integration with vue-router
 * https://vueuse.org/router/useRouteParams/
 * @param name
 * @param def
 * @param decoderTransformer
 * @param encoderTransformer
 * @param args
 */
export default function useQueryEncoder(
  name: string,
  def: any = null,
  decoderTransformer: any = null,
  encoderTransformer: any = null,
  args: Record<string, any> = {}, // Extra arguments for the query encoder
) {
  const holder = isRef(def) ? def : ref(def)

  const qEncoder = new QueryEncoder({
    queryPoint: name,
    decodeOnPop: true,
    defaultVal: unref(def),
    addQueryKeyByDefault: false,
    decoder: ((raw, enc) => {
      const res = typeof decoderTransformer === 'function' ? decoderTransformer(raw, enc) : raw
      // An absent parameter decodes to ''. It falls back to the default, never to null (withDefaults()
      // only applies to undefined); decoded false and 0 are real values and stay.
      holder.value = res === '' || res == null ? (unref(def) ?? undefined) : res
      return res
    }) as any,
    encoder: encoderTransformer,
    ...args,
  })

  const binder = computed({
    get() {
      return holder.value
    },
    set(val) {
      holder.value = val
      qEncoder.data = val
    },
  })

  watchDeep(binder, () => {
    qEncoder.encode()
  })

  // Called outside a component in a few places, so only register the teardown when there is one.
  if (getCurrentScope()) onScopeDispose(() => qEncoder.dispose())

  return binder
}
